import { useQuery } from '@tanstack/react-query'
import http from '@/lib/http'
import { isRoleAllowedToUseDecisionAssistant } from '@/lib/permissions'

/**
 * Decision Assistant — Prescriber SPA side.
 *
 * PA reads the stored validation output through the Node API:
 *
 *   GET {VITE_NODE_API_URL}/prescriber/med-requests/{id}/da-validation
 *
 * The Node API serves the decision persisted in the PHP API (the system of
 * record for validation outputs) with excluded product/variation hashes
 * already resolved to display names — PA no longer calls the DA service.
 *
 * Anything that is not an available, decided validation (unknown item, no
 * stored validation yet, a blocked run, network failure, malformed shape) is
 * treated as *unavailable* — we never render a partial/unknown shape as a
 * real decision.
 */

export type DASeverityTag = 'reject' | 'fan' | 'warning'

export type DAReason = {
  id: string
  text: string
  severity?: DASeverityTag
}

export type DAProductExclusion = {
  id: string
  name: string
  subLabel?: string
}

/** Excluded variations grouped under their parent product, e.g.
 * { productTitle: "Viagra Connect Tablets", variations: ["50mg", "100mg"] }.
 * Only excluded variations are listed. */
export type DAVariationExclusionGroup = {
  productHash: string
  productTitle: string
  variations: string[]
}

/**
 * Suggested action, derived from the validation result via the precedence
 * cascade (see {@link deriveOutcome}):
 *   reject  >  furtherAnswers (FAN / On Hold)  >  review  >  approve
 */
type DAFlaggedOutcome = {
  reasons: DAReason[]
  productExclusions?: DAProductExclusion[]
  variationExclusions?: DAVariationExclusionGroup[]
}

export type DAOutcome =
  | { type: 'approve' }
  | ({ type: 'review' } & DAFlaggedOutcome)
  | ({ type: 'furtherAnswers' } & DAFlaggedOutcome)
  | ({ type: 'reject' } & DAFlaggedOutcome)

export type DAExcludedProduct = {
  hash?: string
  title?: string | null
}

export type DAExcludedVariation = {
  hash?: string
  title?: string | null
  strength_label?: string | null
  product_hash?: string | null
  product_title?: string | null
}

/** The stored validation row served by the Node API. Reason shapes are
 * intentionally loose — we normalise defensively. */
export type DAStoredValidation = {
  order_reference?: string
  action?: 'approve' | 'review' | 'fan' | 'reject' | 'blocked'
  blocked_reason?: string | null
  requires_review?: boolean
  ruleset_version?: string | null
  consultation_version?: string | null
  rejection_reasons?: unknown[]
  fans?: unknown[]
  warnings?: unknown[]
  excluded_products?: DAExcludedProduct[]
  excluded_variations?: DAExcludedVariation[]
  created_at?: string | null
  modified_at?: string | null
}

export type DAValidationResponse = {
  available?: boolean
  validation?: DAStoredValidation | null
}

/**
 * Treat a response as a decided validation only when it is available and not
 * a blocked run (blocked = the DA could not resolve a governing ruleset, so
 * no decision exists to display).
 */
export function isDecidedValidation(data: unknown): data is { available: true; validation: DAStoredValidation } {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  if (d.available !== true) return false
  if (!d.validation || typeof d.validation !== 'object') return false
  return (d.validation as DAStoredValidation).action !== 'blocked'
}

const REASON_TEXT_KEYS = ['text', 'message', 'reason', 'description', 'label', 'name', 'title', 'detail']

function extractText(item: unknown): string | null {
  if (typeof item === 'string') return item.trim() || null
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>
    for (const key of REASON_TEXT_KEYS) {
      const value = obj[key]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
  }
  return null
}

function normalizeReasons(list: unknown, severity: DASeverityTag): DAReason[] {
  if (!Array.isArray(list)) return []
  return list
    .map((item, index): DAReason | null => {
      const text = extractText(item)
      return text ? { id: `${severity}-${index}`, text, severity } : null
    })
    .filter((reason): reason is DAReason => reason !== null)
}

/**
 * Map the enriched excluded products to display exclusions. Prefer the
 * resolved title and fall back to the raw hash, so an exclusion is never
 * silently hidden.
 */
function resolveProductExclusions(list: DAExcludedProduct[] | undefined): DAProductExclusion[] {
  if (!Array.isArray(list)) return []
  return list
    .map((item, index): DAProductExclusion | null => {
      const name = item.title?.trim() || item.hash?.trim()
      return name ? { id: `excl-product-${index}`, name } : null
    })
    .filter((exclusion): exclusion is DAProductExclusion => exclusion !== null)
}

/**
 * Group the enriched excluded variations under their parent product, carrying
 * each variation's strength label. Variations that could not be resolved to a
 * parent product are skipped, since they cannot be grouped or labelled
 * meaningfully.
 *
 * Variations whose parent product is already fully excluded are skipped too —
 * when the whole product is out, we show the product, not its individual
 * strengths.
 */
function resolveVariationExclusions(
  list: DAExcludedVariation[] | undefined,
  excludedProductHashes: Set<string>
): DAVariationExclusionGroup[] {
  if (!Array.isArray(list)) return []
  const groupsByProduct = new Map<string, DAVariationExclusionGroup>()
  for (const item of list) {
    const productHash = item.product_hash?.trim()
    if (!productHash) continue
    const label = item.strength_label?.trim() || item.title?.trim()
    if (!label) continue
    if (excludedProductHashes.has(productHash)) continue
    let group = groupsByProduct.get(productHash)
    if (!group) {
      group = { productHash, productTitle: item.product_title?.trim() || '', variations: [] }
      groupsByProduct.set(productHash, group)
    }
    // Dedupe: a repeated hash, or two hashes sharing a strength label, must not
    // render the same chip twice or inflate the count.
    if (!group.variations.includes(label)) {
      group.variations.push(label)
    }
  }
  return [...groupsByProduct.values()].filter(group => group.variations.length > 0)
}

/**
 * Suggestion logic (precedence cascade):
 *   if (rejections > 0) -> Reject          // hard block
 *   if (fans       > 0) -> Further Answers // FAN = Further Action Needed ("On Hold")
 *   if (warnings   > 0) -> Review          // advisory raised — human should look
 *   if (exclusions   )  -> Review          // products/variations excluded — human should look
 *   else                -> Approve         // nothing flagged
 */
export function deriveOutcome(validation: DAStoredValidation): DAOutcome {
  const rejections = normalizeReasons(validation.rejection_reasons, 'reject')
  const fans = normalizeReasons(validation.fans, 'fan')
  const warnings = normalizeReasons(validation.warnings, 'warning')

  const excludedProductHashes = new Set(
    (validation.excluded_products ?? []).map(item => item.hash?.trim()).filter((hash): hash is string => !!hash)
  )
  const productExclusions = resolveProductExclusions(validation.excluded_products)
  const variationExclusions = resolveVariationExclusions(validation.excluded_variations, excludedProductHashes)
  // Always include the arrays; the panel guards on `?.length`, so empty ones
  // render nothing.
  const hasExclusions = productExclusions.length > 0 || variationExclusions.length > 0

  // Use the array count, but honour an explicit reject action even if the
  // stored row carries no reason list.
  const isRejected = rejections.length > 0 || validation.action === 'reject'

  if (isRejected) {
    const reasons = [...rejections, ...fans, ...warnings]
    return {
      type: 'reject',
      reasons: reasons.length ? reasons : [{ id: 'rejected', text: 'Consultation rejected by validation' }],
      productExclusions,
      variationExclusions
    }
  }

  if (fans.length > 0) {
    return { type: 'furtherAnswers', reasons: [...fans, ...warnings], productExclusions, variationExclusions }
  }

  if (warnings.length > 0) {
    return { type: 'review', reasons: warnings, productExclusions, variationExclusions }
  }

  // No reject/fan/warning, but products/variations were excluded — surface for
  // human review rather than a clean approve. Reasons may be empty here; the
  // panel only renders the Reasons section when there are reasons to show.
  if (hasExclusions) {
    return { type: 'review', reasons: [], productExclusions, variationExclusions }
  }

  return { type: 'approve' }
}

/**
 * Fetches the stored Decision Assistant validation for a medication request
 * from the Node API and derives the suggested outcome.
 *
 * On any failure (unavailable / blocked / network / malformed) the query
 * THROWS — it does not resolve to a sentinel. This lets react-query keep the
 * last good result through a transient refetch error (so a real decision never
 * silently flips to "not available" on a blip), and `isError` is the unavailable
 * signal.
 *
 * The request is additionally gated on the user holding a role allowed to see
 * DA output, independently of `enabled`.
 */
export function useDecisionAssistant(medicationRequestId?: string, enabled = true) {
  const query = useQuery<DAOutcome>({
    queryKey: ['decisionAssistant', medicationRequestId],
    queryFn: async () => {
      const data = await http.get(`/prescriber/med-requests/${medicationRequestId}/da-validation`)
      if (!isDecidedValidation(data)) throw new Error('Decision Assistant unavailable')
      return deriveOutcome(data.validation)
    },
    enabled: enabled && !!medicationRequestId && isRoleAllowedToUseDecisionAssistant(),
    retry: false
  })

  return {
    isLoading: query.isLoading,
    outcome: query.data,
    // Unavailable only when we errored with no previously-good result to show.
    isUnavailable: query.isError && query.data === undefined
  }
}
